import Keycloak, {
	type KeycloakServerConfig as KeycloakInitOptions,
} from "keycloak-js";

import { BaseAuthStrategy } from "./base-strategy";
import { AuthFeature } from "./types";
import type { AuthResult, RegisteredUser } from "./types";

export interface KeycloakCredentials {
	username?: string;
	password?: string;
}

export interface KeycloakConfig extends KeycloakInitOptions {
	// Additional config options can be added here
	enableLogging?: boolean;
	checkLoginIframe?: boolean;
}

export class KeycloakStrategy extends BaseAuthStrategy {
	readonly type = "keycloak";
	readonly name = "Keycloak";
	readonly description = "Keycloak authentication via authorization code flow";

	private keycloak: Keycloak;
	private initialized = false;
	private initPromise: Promise<boolean> | null = null;

	constructor(private config: KeycloakConfig) {
		super();
		this.keycloak = new Keycloak({
			url: config.url,
			realm: config.realm,
			clientId: config.clientId,
		});
	}

	private async ensureInitialized(): Promise<boolean> {
		if (this.initialized) {
			return this.keycloak.authenticated || false;
		}

		if (this.initPromise) {
			return this.initPromise;
		}

		this.initPromise = this.keycloak.init({
			onLoad: "login-required", // Force login on initialization
			// onLoad: "check-sso", // Check if user is already logged in
			// silentCheckSsoRedirectUri:
			// window.location.origin + "/silent-check-sso.html",
			checkLoginIframe: this.config.checkLoginIframe ?? false,
			enableLogging: this.config.enableLogging ?? false,
		});

		try {
			const authenticated = await this.initPromise;
			this.initialized = true;
			return authenticated;
		} catch (error) {
			this.initPromise = null;
			throw error;
		}
	}

	async authenticate(
		_credentials?: unknown,
		options?: Record<string, unknown>,
	): Promise<AuthResult> {
		try {
			// Ensure Keycloak is initialized
			const authenticated = await this.ensureInitialized();

			// If not authenticated, trigger login
			if (!authenticated) {
				// The login method will redirect to Keycloak, so this won't return
				this.keycloak.login({
					redirectUri: options?.redirectUri?.toString() || window.location.href,
				});

				// This code won't be reached as the page will redirect
				return {
					success: false,
					error: "Redirecting to Keycloak for authentication",
					details: { redirecting: true },
				};
			}
			// Get user info from the token
			const userInfo = this.keycloak.tokenParsed;
			if (!userInfo) {
				return {
					success: false,
					error: "Failed to parse user information from token",
				};
			}

			// Map Keycloak's identity onto our RegisteredUser shape. This describes the
			// KEYCLOAK account, not the app user: `id` is Keycloak's subject, which is
			// not necessarily the app user id, and the status below is a placeholder the
			// type demands rather than a fact. `login()` resolves the real app user (and
			// its true registrationStatus) via authApi.resolveUser — don't rely on
			// these two fields past that point.
			const user: RegisteredUser = {
				id: userInfo.sub || userInfo.preferred_username || "",
				email: userInfo.email || "",
				registrationStatus: "Completed",
			};

			return {
				success: true,
				user,
				token: this.keycloak.token,
				refreshToken: this.keycloak.refreshToken,
			};
		} catch (error) {
			return this.handleError(error);
		}
	}

	async logout(): Promise<void> {
		try {
			await this.ensureInitialized();

			await this.keycloak.logout({
				redirectUri: window.location.origin,
			});
		} catch (_error) {
			console.error("Keycloak logout failed", _error);
			// Keycloak logout failed, but don't throw to avoid breaking the app
		}
	}

	async refresh(): Promise<AuthResult> {
		try {
			const refreshed = await this.keycloak.updateToken(30); // Refresh if token expires in 30 seconds

			if (refreshed) {
				// Token was refreshed, get updated user info
				const userInfo = this.keycloak.tokenParsed;
				if (!userInfo) {
					return {
						success: false,
						error: "Failed to parse user information from refreshed token",
					};
				}

				const user: RegisteredUser = {
					id: userInfo.sub || userInfo.preferred_username || "",
					email: userInfo.email || "",
					registrationStatus: "Completed",
				};

				return {
					success: true,
					user,
					token: this.keycloak.token,
					refreshToken: this.keycloak.refreshToken,
				};
			} else {
				// Token is still valid
				const userInfo = this.keycloak.tokenParsed;
				if (!userInfo) {
					return {
						success: false,
						error: "Failed to parse user information from token",
					};
				}

				const user: RegisteredUser = {
					id: userInfo.sub || userInfo.preferred_username || "",
					email: userInfo.email || "",
					registrationStatus: "Completed",
				};

				return {
					success: true,
					user,
					token: this.keycloak.token,
					refreshToken: this.keycloak.refreshToken,
				};
			}
		} catch (error) {
			return this.handleError(error);
		}
	}

	async validate(): Promise<boolean> {
		try {
			if (!this.initialized) {
				await this.ensureInitialized();
			}
			// Check if token is still valid
			return !this.keycloak.isTokenExpired();
		} catch {
			return false;
		}
	}

	isAvailable(): boolean {
		// Check if we're in a browser environment
		return typeof window !== "undefined";
	}

	getRequiredFields(): string[] {
		// Keycloak handles authentication via redirect, no direct fields needed
		return [];
	}

	supportsFeature(feature: AuthFeature): boolean {
		switch (feature) {
			case AuthFeature.PASSWORD_RESET:
			case AuthFeature.EMAIL_VERIFICATION:
				return true; // Keycloak handles these
			default:
				return false;
		}
	}

	// Helper method to check if user is authenticated
	isAuthenticated(): boolean {
		return this.keycloak.authenticated || false;
	}

	// Helper method to get current user info
	getCurrentUser(): RegisteredUser | null {
		const userInfo = this.keycloak.tokenParsed;
		if (!userInfo) {
			return null;
		}

		return {
			id: userInfo.sub || userInfo.preferred_username || "",
			email: userInfo.email || "",
			registrationStatus: "Completed",
		};
	}

	// Helper method to get current token
	getCurrentToken(): string | undefined {
		return this.keycloak.token;
	}

	// Helper method to check if current URL contains Keycloak redirect parameters
	isKeycloakRedirect(): boolean {
		// Check query parameters (for some OAuth2 flows)
		const urlParams = new URLSearchParams(window.location.search);
		const hasQueryParams = urlParams.has("code") && urlParams.has("state");

		// Check fragment parameters (for implicit/hybrid flows)
		const fragment = window.location.hash.substring(1); // Remove the '#'
		const fragmentParams = new URLSearchParams(fragment);
		const hasFragmentParams =
			fragmentParams.has("code") && fragmentParams.has("state");

		return hasQueryParams || hasFragmentParams;
	}

	// Method to handle redirect back from Keycloak
	async handleRedirect(): Promise<AuthResult> {
		if (!this.isKeycloakRedirect()) {
			return {
				success: false,
				error: "No Keycloak redirect parameters found",
			};
		}

		try {
			// Force initialization to process the redirect
			this.initialized = false;
			this.initPromise = null;

			const authenticated = await this.ensureInitialized();

			if (authenticated) {
				// Get user info from the token
				const userInfo = this.keycloak.tokenParsed;

				if (!userInfo) {
					return {
						success: false,
						error: "Failed to parse user information from token",
					};
				}

				// Map Keycloak user to our RegisteredUser format
				const user: RegisteredUser = {
					id: userInfo.sub || userInfo.preferred_username || "",
					email: userInfo.email || "",
					registrationStatus: "Completed",
				};

				return {
					success: true,
					user,
					token: this.keycloak.token,
					refreshToken: this.keycloak.refreshToken,
				};
			} else {
				return {
					success: false,
					error: "Authentication failed after redirect",
				};
			}
		} catch (error) {
			return this.handleError(error);
		}
	}
}
